import { formatMoney } from '../../utils/helpers/aiSpendFormat';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Weekday x hour, as a plain CSS grid.
 *
 * chart.js has no matrix chart without a plugin, and a grid of divs is both
 * smaller and trivially dark-mode aware. Hours are the server's, computed in
 * the reporting timezone — a raw UTC EXTRACT would put "the 2pm peak" five
 * hours away from when it actually happened.
 */
export default function SpendHeatmap({ cells, maxCost, timezone, loading }) {
  const byKey = new Map(
    (cells || []).map((cell) => [`${cell.weekday}:${cell.hour}`, cell]),
  );

  if (loading) {
    return <div className="h-48 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-700" />;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="min-w-[46rem]">
        <div className="grid grid-cols-[3rem_repeat(24,1fr)] gap-0.5">
          <span />
          {Array.from({ length: 24 }).map((_, hour) => (
            <span
              key={hour}
              className="text-center text-[10px] text-slate-400"
              aria-hidden="true"
            >
              {hour % 3 === 0 ? hour : ''}
            </span>
          ))}
          {WEEKDAYS.map((day, dayIndex) => (
            <ChartRow
              key={day}
              day={day}
              weekday={dayIndex + 1}
              byKey={byKey}
              maxCost={maxCost}
            />
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Hours are in {timezone || 'the reporting timezone'}.
        </p>
      </div>
    </div>
  );
}

function ChartRow({ day, weekday, byKey, maxCost }) {
  return (
    <>
      <span className="pr-2 text-right text-[11px] text-slate-500">{day}</span>
      {Array.from({ length: 24 }).map((_, hour) => {
        const cell = byKey.get(`${weekday}:${hour}`);
        const cost = cell?.cost_usd || 0;
        const intensity = maxCost > 0 ? Math.min(cost / maxCost, 1) : 0;
        return (
          <span
            key={hour}
            role="img"
            aria-label={`${day} ${hour}:00 — ${formatMoney(cost)}, ${cell?.calls || 0} calls`}
            title={`${day} ${hour}:00 — ${formatMoney(cost)} · ${cell?.calls || 0} calls`}
            className="h-5 rounded-sm bg-blue-600"
            style={{ opacity: intensity === 0 ? 0.06 : 0.15 + intensity * 0.85 }}
          />
        );
      })}
    </>
  );
}
